import useCompanies from 'hooks/useCompanies';
import useEquipmentData from 'hooks/useEquipmentData';
import usePeopleData from 'hooks/usePeopleData';
import useTagsData from 'hooks/useTagsData';
import { useEffect } from 'react';
import { Tag } from 'screens/Applications&WorkOrders/StepsLayout/Components/DetailsStep/components/TagsSection';
import {
  ApplicationDetails,
  ApplicationMethod,
  TimingEvent
} from 'screens/Applications&WorkOrders/Applications/Hooks/useApplication';
import { useStore } from 'components/Store';
import {
  CropSeason,
  RecordSource
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import { OperationType } from 'components/RecordsListView/constants';
import { formatApplicationDetailsTabContent } from '../helpers';

type Authorizer = {
  id: string;
  name: string;
};

type UsePeopleData = {
  people: {
    id: string;
    name: string;
    licenseNumber?: string;
    expirationDate?: string;
  }[];
  loading: boolean;
  loadPeople: () => void;
};

type UseCompaniesData = {
  companies: { id: string; name: string }[];
  loading: boolean;
  loadCompanies: () => void;
};

export type Applicator = {
  key?: string;
  name?: string;
  licenseNumber?: string;
  company?: string;
  expirationDate?: string;
};

export type ApplicationDetailsTabData = {
  applicators?: Array<Applicator>;
  operationId?: string;
  name: string;
  timingEvent?: TimingEvent;
  applicationMethod?: ApplicationMethod;
  equipments: string[];
  reiExpirationDate?: string;
  phiExpirationDate?: string;
  recordSource?: RecordSource;
  createdAt: string;
  createdBy: string;
  authorizedBy?: Authorizer;
  authorizationDate: string;
  cropSeasons: string[];
  tags: Tag[];
};

const useDetailsTabData = (
  applicationDetails: ApplicationDetails,
  type: OperationType = OperationType.APPLICATION
) => {
  const { state } = useStore();

  const { cropSeasons } = state;
  const { equipments, loading: isLoadingEquipments } = useEquipmentData();

  const {
    people,
    loading: isLoadingPeople,
    loadPeople
  } = usePeopleData() as UsePeopleData;

  const {
    companies,
    loading: isLoadingCompanies,
    loadCompanies
  } = useCompanies() as UseCompaniesData;

  const { tags, loading: isLoadingTags, getTags } = useTagsData();

  const foundEquipments = equipments
    .filter(equipment =>
      applicationDetails.equipmentIds?.includes(equipment.id)
    )
    .map(equipment => equipment.name);

  // `authorization` is typed required but the API omits it when unauthorized.
  // The id is checked first too: undefined would match an undefined id.
  const authorizerId = applicationDetails.authorization?.authorizerId;

  const foundAuthorizer = authorizerId
    ? ([...people, ...companies] as Authorizer[]).find(
        authorizer => authorizer.id === authorizerId
      )
    : undefined;

  const foundCropSeasons = (cropSeasons as CropSeason[])
    ?.filter(cropSeason =>
      applicationDetails.cropSeasonIds?.includes(cropSeason.id)
    )
    .map(cropSeason => cropSeason.name);

  const applicators =
    applicationDetails.applicators?.map((applicator, index) => {
      // Table row key: applicator entries have no id of their own.
      const key = `${applicator.personId ?? ''}-${applicator.companyId ??
        ''}-${index}`;
      const foundPerson = people.find(
        person => person.id === applicator.personId
      );
      const foundCompany = companies.find(
        company => company.id === applicator.companyId
      );

      if (!foundPerson && !foundCompany) return { key };

      return {
        key,
        name: foundPerson?.name,
        licenseNumber: foundPerson?.licenseNumber,
        expirationDate: foundPerson?.expirationDate,
        company: foundCompany?.name
      };
    }) ?? [];

  const foundTags = tags.filter(tag =>
    applicationDetails?.tagIds?.includes(tag.id)
  );

  const createdBy =
    people.find(person => person.id === applicationDetails.createdBy)?.name ??
    '';

  useEffect(() => {
    loadPeople();
    loadCompanies();
    getTags();
  }, [loadCompanies, loadPeople, getTags]);

  const data: ApplicationDetailsTabData = {
    applicators,
    operationId: applicationDetails.id,
    name: applicationDetails.name,
    timingEvent: applicationDetails.timingEvent,
    applicationMethod: applicationDetails.applicationMethod,
    equipments: foundEquipments,
    recordSource: applicationDetails.recordSource,
    reiExpirationDate: applicationDetails.reiExpirationDate,
    phiExpirationDate: applicationDetails.phiExpirationDate,
    createdAt: applicationDetails.createdAt,
    createdBy,
    authorizedBy: foundAuthorizer,
    // Empty string, not undefined: formatAuthorizationDate renders '---'.
    authorizationDate:
      applicationDetails.authorization?.authorizationDate ?? '',
    cropSeasons: foundCropSeasons,
    tags: foundTags
  };

  const formattedData = formatApplicationDetailsTabContent(data, type);

  const isLoading =
    isLoadingEquipments ||
    isLoadingPeople ||
    isLoadingCompanies ||
    isLoadingTags;

  return {
    data: formattedData,
    isLoading
  };
};

export default useDetailsTabData;
